import { formatDate } from "@/utils/date";
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { ArrowRightIcon, ArrowUpDownIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage } from "../../common/Error";
import { ShimmerTable } from "../../common/Table";
import Text from "../../common/Text";
import { ScheduledJob } from "@/types/common.types";
import { cn } from "@/lib/utils";
import { Button } from "../../ui/button";
import { VirtualTable } from "@/components/common/VirtualTable";
import { format, getDaysInMonth } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import { useGenerateInsightMutation } from "@/hooks/mutations";
import { useGetRolesQuery, useGetScheduledJobsQuery } from "@/hooks/queries";
import { COLOR_MAP, PAGE_SIZE, STATUS_DISPLAY_MAP } from "@/lib/constants";
import { CustomAlert } from "@/components/ui/Alert";

type JobsListTableProps = {
  onJobClick: (job: ScheduledJob) => void;
  search: string;
  setSearch: (search: string) => void;
  debouncedSearchValue: string;
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  setColumnOptions: (options: Array<{ id: string; label: string }>) => void;
  setInitialSelectedColumns: (columns: string[]) => void;
};

const INSIGHTS_LIST_TABLE_COLUMNS_LABELS = {
  jobName: "Dataset",
  totalStyleIds: "Total Style Ids",
  label: "Status",
  startTime: "Start Date",
  endTime: "End Date",
  frequency: "Frequency",
  lastExecutionTime: "Last Fetched Time",
  actions: "Action",
};

const Badge = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      "inline-block rounded-none px-2 py-1 text-xs leading-[14px]",
      className
    )}
  >
    {children}
  </span>
);
const Pill = ({ status }: { status: keyof typeof COLOR_MAP }) => {
  return (
    <Badge
      className={cn(
        "items-center justify-start rounded-none px-2 py-1 text-xs font-normal",
        COLOR_MAP[status]
      )}
    >
      {STATUS_DISPLAY_MAP[status]}
    </Badge>
  );
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const InsightsCell = ({
  jobId,
  jobName,
  totalStyleIds,
  startTime,
}: {
  jobId: string;
  jobName: string;
  totalStyleIds: number;
  startTime: string;
}) => {
  const { sendEvent } = useAnalyticsManager();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Get job start date
  const jobStartDate = new Date(startTime || new Date());
  const jobStartYear = jobStartDate.getFullYear();
  const jobStartMonth = jobStartDate.getMonth();
  const jobStartDay = jobStartDate.getDate();

  // Get yesterday's date
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayYear = yesterday.getFullYear();
  const yesterdayMonth = yesterday.getMonth();
  const yesterdayDay = yesterday.getDate();

  const [startMonth, setStartMonth] = useState<number>(jobStartMonth);
  const [startYear, setStartYear] = useState<number>(jobStartYear);
  const [startDay, setStartDay] = useState<number>(jobStartDay);
  const [endMonth, setEndMonth] = useState<number>(yesterdayMonth);
  const [endYear, setEndYear] = useState<number>(yesterdayYear);
  const [endDay, setEndDay] = useState<number>(yesterdayDay);
  const { mutate: generateInsight, isPending: isGeneratingInsight } =
    useGenerateInsightMutation({
      onSuccess: () => {
        CustomAlert.success({
          description: "Insights generated successfully.",
        });
      },
      onError: (error) => {
        // The exact backend message is surfaced by the global 400 handler in
        // the ky client; just log here to avoid a duplicate toast.
        console.log("Error generating insight", error);
      },
    });

  const getDaysArray = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(new Date(year, month));
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  const startDays = getDaysArray(startYear, startMonth);
  const endDays = getDaysArray(endYear, endMonth);

  // Get available years based on job start date and yesterday
  const availableYears = Array.from(
    { length: yesterdayYear - jobStartYear + 1 },
    (_, i) => jobStartYear + i
  );

  const handleGenerateInsights = () => {
    const startDate = new Date(startYear, startMonth, startDay);
    const endDate = new Date(endYear, endMonth, endDay);

    // Validate date range
    if (startDate < jobStartDate) {
      CustomAlert.error({
        description: "Start date cannot be before the job's start date",
      });
      return;
    }
    if (endDate > yesterday) {
      CustomAlert.error({
        description: "End date cannot be greater than yesterday",
      });
      return;
    }
    if (endDate < startDate) {
      CustomAlert.error({
        description: "End date cannot be before start date",
      });
      return;
    }

    sendEvent("generate_insights_clicked", {
      dataset_id: jobId,
      dataset_name: jobName,
      job_id: jobId,
      total_style_ids: totalStyleIds,
    });

    generateInsight({
      jobId,
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    });

    setIsDatePickerOpen(false);
  };

  return (
    <>
      <div className="flex items-center justify-start">
        <Button
          variant="outline"
          rightIcon={<ArrowRightIcon size={14} />}
          className="h-8 gap-1 border-0 bg-brand-accent px-3 text-[11px] text-white hover:text-white disabled:opacity-50"
          onClick={() => setIsDatePickerOpen(true)}
          isLoading={isGeneratingInsight}
        >
          Generate Insights
        </Button>
      </div>

      <Dialog open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Select Date Range for Insights</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Text
                  variant="paragraph_p6"
                  className="text-shades-neutral-300"
                >
                  Start Date
                </Text>
                <div className="flex gap-2">
                  <select
                    className="flex h-10 w-[120px] rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={startMonth}
                    onChange={(e) => setStartMonth(Number(e.target.value))}
                  >
                    {months.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex h-10 w-[80px] rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={startDay}
                    onChange={(e) => setStartDay(Number(e.target.value))}
                  >
                    {startDays.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex h-10 w-[100px] rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={startYear}
                    onChange={(e) => setStartYear(Number(e.target.value))}
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Text
                  variant="paragraph_p6"
                  className="text-shades-neutral-300"
                >
                  End Date
                </Text>
                <div className="flex gap-2">
                  <select
                    className="flex h-10 w-[120px] rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={endMonth}
                    onChange={(e) => setEndMonth(Number(e.target.value))}
                  >
                    {months.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex h-10 w-[80px] rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={endDay}
                    onChange={(e) => setEndDay(Number(e.target.value))}
                  >
                    {endDays.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex h-10 w-[100px] rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={endYear}
                    onChange={(e) => setEndYear(Number(e.target.value))}
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDatePickerOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateInsights}
              disabled={isGeneratingInsight}
              isLoading={isGeneratingInsight}
              className="text-white"
            >
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const InsightsListTable: React.FC<JobsListTableProps> = ({
  onJobClick,
  search,
  setSearch,
  debouncedSearchValue,
  columnVisibility,
  setColumnOptions,
  setInitialSelectedColumns,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetScheduledJobsQuery(PAGE_SIZE, debouncedSearchValue);
  const { sendEvent } = useAnalyticsManager();
  const { data: roleData } = useGetRolesQuery();
  useEffect(() => {
    if (debouncedSearchValue) {
      sendEvent("job_search_initiated", {
        search_query: debouncedSearchValue,
        screen_name: "insights list table",
      });
    }
  }, [debouncedSearchValue]);

  const jobListColumns: ColumnDef<ScheduledJob>[] = useMemo(() => {
    const baseColumns: ColumnDef<ScheduledJob>[] = [
      {
        accessorKey: "jobName",
        header: "Dataset",
        cell: ({ row }) => {
          return (
            <div className="cursor-pointer text-left capitalize text-brand-accent underline">
              {row.original.jobName}
            </div>
          );
        },
      },
      {
        accessorKey: "totalStyleIds",
        header: "Total Style Ids",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.totalStyleIds}
          </div>
        ),
      },
      {
        accessorKey: "label",
        header: "Status",
        cell: ({ row }) => (
          <div className="flex justify-start">
            <Pill
              status={
                row.original.scheduledJobStatus as
                  | "SUCCESS"
                  | "FAILURE"
                  | "INPROGRESS"
              }
            />
          </div>
        ),
      },
      {
        accessorKey: "startTime",
        header: ({ column }) => (
          <div
            className="flex cursor-pointer items-center justify-start gap-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="ml-2 h-4 w-4" />
            Start Date
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {formatDate(row.original.startTime)}
          </div>
        ),
      },
      {
        accessorKey: "endTime",
        header: ({ column }) => (
          <div
            className="flex cursor-pointer items-center justify-start gap-2"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="ml-2 h-4 w-4" />
            End Date
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {formatDate(row.original.endTime)}
          </div>
        ),
      },
      {
        accessorKey: "frequency",
        header: "Frequency",
        cell: ({ row }) => {
          return (
            <div className="text-left capitalize">
              {row.original.frequency}
            </div>
          );
        },
      },
      {
        accessorKey: "lastExecutionTime",
        header: "Last Fetched Time",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {formatDate(row.original.lastExecutionTime)}
          </div>
        ),
      },
      {
        id: "actions",
        header: "Action",
        cell: ({ row }) => {
          return (
            <div className="flex items-center justify-start">
              <Button
                variant="outline"
                rightIcon={<ArrowRightIcon size={14} />}
                className="h-8 gap-1 border border-brand-accent px-3 text-[11px] text-brand-accent hover:bg-brand-accent hover:text-white"
                onClick={() => onJobClick(row.original)}
              >
                View
              </Button>
            </div>
          );
        },
      },
    ];

    const isAdminOrOwner =
      roleData?.roles.some((role) => role.includes("OWNER")) ||
      roleData?.isAdmin;

    if (isAdminOrOwner) {
      // Insert the insights column before the actions column
      baseColumns.splice(baseColumns.length - 1, 0, {
        id: "insights",
        header: "Insights",
        cell: ({ row }) => (
          <InsightsCell
            jobId={row.original.id.toString()}
            jobName={row.original.jobName}
            totalStyleIds={row.original.totalStyleIds}
            startTime={row.original.startTime || ""}
          />
        ),
      });
    }

    return baseColumns;
  }, [onJobClick, roleData?.roles, roleData?.isAdmin]);
  const tableData = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data?.pages]);

  const table = useReactTable({
    data: tableData,
    columns: jobListColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility,
    },
  });
  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (table) {
      const options = table
        .getAllColumns()
        .filter((column) => column.getCanHide())
        .map((column) => ({
          id: column.id,
          label:
            INSIGHTS_LIST_TABLE_COLUMNS_LABELS[
              column.id as keyof typeof INSIGHTS_LIST_TABLE_COLUMNS_LABELS
            ] || column.id,
        }));
      setColumnOptions(options);

      const selected = table
        .getAllColumns()
        .filter((column) => column.getIsVisible())
        .map((column) => column.id);
      setInitialSelectedColumns(selected);
    }
  }, [table, setColumnOptions, setInitialSelectedColumns]);

  if (isLoading) {
    return (
      <JobListHeader search={search} setSearch={setSearch} totalJobs={0}>
        <ShimmerTable<ScheduledJob> columns={jobListColumns} rowCount={5} />
      </JobListHeader>
    );
  }

  if (isError) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <>
      <JobListHeader
        search={search}
        setSearch={setSearch}
        totalJobs={data?.pages?.[0]?.totalElements || 0}
      >
        <VirtualTable
          table={table}
          loadMore={loadMore}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          columns={jobListColumns}
        />
      </JobListHeader>
    </>
  );
};

export default InsightsListTable;

type JobListHeaderProps = {
  search: string;
  setSearch: (search: string) => void;
  totalJobs: number;
  children: React.ReactNode;
};
const JobListHeader: React.FC<JobListHeaderProps> = ({
  children,
  totalJobs,
}) => {
  return (
    <>
      {totalJobs > 0 ? (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing {totalJobs} Dataset
          </Text>
        </p>
      ) : (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing 0 Dataset
          </Text>
        </p>
      )}
      {children}
    </>
  );
};
